import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { CalendarDays, Clock, RefreshCw, AlertTriangle } from 'lucide-react';

interface Subscription {
  id: number;
  clientName: string;
  clientAvatar: string;
  subscriptionType: 'Premium' | 'Regular' | '30-hour package' | '10-hour package' | 'Daily' | 'Hourly';
  startDate: string;
  endDate: string;
  hoursLeft: number;
  totalHours: number;
  status: 'Active' | 'Expiring' | 'Expired';
  autoRenew: boolean;
}

export function SubscriptionManagement() {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([
    {
      id: 1,
      clientName: 'John Doe',
      clientAvatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face',
      subscriptionType: 'Premium',
      startDate: '2024-01-15',
      endDate: '2024-07-15',
      hoursLeft: 45,
      totalHours: 60,
      status: 'Active',
      autoRenew: true,
    },
    {
      id: 2,
      clientName: 'Sarah Wilson',
      clientAvatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face',
      subscriptionType: '30-hour package',
      startDate: '2024-02-20',
      endDate: '2024-08-20',
      hoursLeft: 22,
      totalHours: 30,
      status: 'Active',
      autoRenew: false,
    },
    {
      id: 3,
      clientName: 'Mike Johnson',
      clientAvatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
      subscriptionType: 'Premium',
      startDate: '2024-01-10',
      endDate: '2024-07-10',
      hoursLeft: 38,
      totalHours: 60,
      status: 'Active',
      autoRenew: true,
    },
    {
      id: 4,
      clientName: 'Emma Brown',
      clientAvatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face',
      subscriptionType: '10-hour package',
      startDate: '2024-03-05',
      endDate: '2024-09-05',
      hoursLeft: 8,
      totalHours: 10,
      status: 'Active',
      autoRenew: false,
    },
    {
      id: 5,
      clientName: 'Alex Turner',
      clientAvatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&h=150&fit=crop&crop=face',
      subscriptionType: 'Regular',
      startDate: '2024-06-01',
      endDate: '2024-06-21',
      hoursLeft: 12,
      totalHours: 20,
      status: 'Expiring',
      autoRenew: false,
    },
    {
      id: 6,
      clientName: 'Lisa Chen',
      clientAvatar: 'https://images.unsplash.com/photo-1517841905240-472988babdf9?w=150&h=150&fit=crop&crop=face',
      subscriptionType: '30-hour package',
      startDate: '2024-05-15',
      endDate: '2024-06-20',
      hoursLeft: 5,
      totalHours: 30,
      status: 'Expiring',
      autoRenew: false,
    },
  ]);

  const [filterStatus, setFilterStatus] = useState<string>('all');

  const filteredSubscriptions = subscriptions.filter(sub => 
    filterStatus === 'all' || sub.status.toLowerCase() === filterStatus
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active':
        return 'bg-green-100 text-green-800';
      case 'Expiring':
        return 'bg-orange-100 text-orange-800';
      case 'Expired':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'Premium':
        return 'bg-purple-100 text-purple-800';
      case 'Regular':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const renewSubscription = (id: number) => {
    setSubscriptions(subscriptions.map(sub => 
      sub.id === id 
        ? { 
            ...sub, 
            status: 'Active' as const,
            endDate: new Date(Date.now() + 180 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            hoursLeft: sub.totalHours 
          }
        : sub
    ));
  };

  const toggleAutoRenew = (id: number) => {
    setSubscriptions(subscriptions.map(sub => 
      sub.id === id ? { ...sub, autoRenew: !sub.autoRenew } : sub
    ));
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  const stats = {
    total: subscriptions.length,
    active: subscriptions.filter(s => s.status === 'Active').length,
    expiring: subscriptions.filter(s => s.status === 'Expiring').length,
    expired: subscriptions.filter(s => s.status === 'Expired').length,
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm">Total Subscriptions</CardTitle>
            <CalendarDays className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl">{stats.total}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm">Active</CardTitle>
            <Clock className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl text-green-600">{stats.active}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm">Expiring Soon</CardTitle>
            <AlertTriangle className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl text-orange-600">{stats.expiring}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm">Expired</CardTitle>
            <AlertTriangle className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl text-red-600">{stats.expired}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filter and Actions */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Subscriptions</SelectItem>
              <SelectItem value="active">Active Only</SelectItem>
              <SelectItem value="expiring">Expiring Soon</SelectItem>
              <SelectItem value="expired">Expired</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Subscriptions Table */}
      <Card>
        <CardHeader>
          <CardTitle>Subscription Overview</CardTitle>
          <CardDescription>Manage all client subscriptions and renewals</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Client</TableHead>
                <TableHead>Subscription Type</TableHead>
                <TableHead>Start Date</TableHead>
                <TableHead>End Date</TableHead>
                <TableHead>Hours Left</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Auto Renew</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredSubscriptions.map((subscription) => (
                <TableRow key={subscription.id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar className="h-8 w-8">
                        <AvatarImage 
                          src={subscription.clientAvatar} 
                          alt={subscription.clientName}
                        />
                        <AvatarFallback>
                          {getInitials(subscription.clientName)}
                        </AvatarFallback>
                      </Avatar>
                      <span>{subscription.clientName}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={getTypeColor(subscription.subscriptionType)}>
                      {subscription.subscriptionType}
                    </Badge>
                  </TableCell>
                  <TableCell>{subscription.startDate}</TableCell>
                  <TableCell>{subscription.endDate}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className={subscription.hoursLeft <= 10 ? 'text-red-600' : ''}>
                        {subscription.hoursLeft}
                      </span>
                      <span className="text-muted-foreground">/ {subscription.totalHours}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={getStatusColor(subscription.status)}>
                      {subscription.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => toggleAutoRenew(subscription.id)}
                    >
                      {subscription.autoRenew ? 'On' : 'Off'}
                    </Button>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      {(subscription.status === 'Expiring' || subscription.status === 'Expired') && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => renewSubscription(subscription.id)}
                        >
                          <RefreshCw className="h-4 w-4 mr-1" />
                          Renew
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}